/**
 * Sessions List Page - Entry point of the application
 * Displays all past sessions in a grid layout with search/filter capabilities
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../store/store';
import { useNewSession } from '../../../hooks/useNewSession';
import {
  deleteSession,
  fetchAllSessions,
  fetchValidatedSessions,
  fetchSessions,
  setIncludeArchivedSessions,
  setViewAllSessions,
  setViewValidatedSessions,
  SessionListDTO,
} from '../../../store/sessionSlice';
import { showSuccess, showError } from '../../../store/toastsSlice';
import SessionCard from './SessionCard';
import SessionPaginationComponent from './SessionPagination';
import {
  deleteAllCountsToConfirmation,
  filterSessions,
  hasActiveSessionFilters,
  itemsToPagination,
  SESSION_REVIEW_FILTER_LABELS_BY_ROLE,
  SESSION_REVIEW_FILTERS_BY_ROLE,
  SESSIONS_PER_PAGE,
  SessionReviewFilter,
  SessionReviewRole,
  selectedIdsToCanCompare,
  selectedIdsToCompareLabel,
  selectedIdsToComparePath,
  filterMergeableSessions,
  selectedIdsToMergeSelectionView,
  sessionsToAvailableSites,
  sessionsToDeletable,
  sortSessionsNewestFirst,
} from './dataTransformations';
import { withCondition } from '../../../utils/withCondition';
import useSessionListControls from './useSessionListControls';
import useSessionMergeSelection from './useSessionMergeSelection';
import { SessionMergeSelection } from './SessionMergeSelection';
import SessionMergeToolbarComponent from './SessionMergeToolbar';
import SessionMergeModeToggleComponent from './SessionMergeModeToggle';
import SessionFilterToggleComponent from './SessionFilterToggle';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import Icon from '@amzn/meridian/icon';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import documentTokens from '@amzn/meridian-tokens/base/icon/document';
import SearchField from '@amzn/meridian/search-field';
import DateRangePicker from '@amzn/meridian/date-range-picker';
import Select, { SelectOption } from '@amzn/meridian/select';
import Row from '@amzn/meridian/row';
import MeridianPaginationComponent from '@amzn/meridian/pagination';
import { FONT_SIZE_BODY, FONT_SIZE_TITLE, FONT_SIZE_EMPTY_STATE_ICON } from '../../shared/cssConstants';
import './SessionsList.css';

// ===== COMPONENT =====

type Pagination = Omit<
  React.ComponentProps<typeof MeridianPaginationComponent>,
  'aria-label'
>;

const SessionPaginationWithCondition = withCondition(
  SessionPaginationComponent,
);
const SessionMergeToolbarWithCondition = withCondition(SessionMergeToolbarComponent);
const SessionMergeModeToggleWithCondition = withCondition(SessionMergeModeToggleComponent);
const SessionFilterToggleWithCondition = withCondition(SessionFilterToggleComponent);

const MERGE_MODE_LABEL = 'Merge mode';
const HIDE_UNMERGEABLE_LABEL = "Hide sessions that can't be merged";
const INCLUDE_ARCHIVED_LABEL = 'Show Archived';
const MERGE_SELECTION_LABEL = 'Select for merge';
const MERGE_TOOLBAR_HINT = 'Merge mode is on. Pick two sessions from the same incident to compare and merge.';

const SessionsList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    ownerSessions, ownerSessionsStatus,
    allSessions, allSessionsStatus, allSessionsNextToken, allSessionsLoading,
    validatedSessions, validatedSessionsStatus,
    viewAllSessions, viewValidatedSessions: viewValidatedChoice,
    includeArchivedSessions,
    error,
  } = useAppSelector((state) => state.session);
  const {
    isAdmin: hasElevatedAccess,
    isEngineer: canDelete,
    isAuditor,
  } = useAppSelector((state) => state.user);

  const {
    searchQuery,
    dateRange,
    selectedSites,
    selectedReviewStates,
    currentPage,
    setSearchQuery,
    setDateRange,
    setSelectedSites,
    setSelectedReviewStates,
    setCurrentPage,
    resetControls,
  } = useSessionListControls();
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState('');
  const {
    isMergeMode,
    selectedIds: mergeSelectedIds,
    hideUnmergeable,
    setMergeMode,
    setHideUnmergeable,
    toggleSelection: toggleMergeSelection,
  } = useSessionMergeSelection();

  const startNewSession = useNewSession();

  // Auditors land on the validated list — it is their work list — until they say otherwise.
  const viewValidatedSessions = viewValidatedChoice ?? isAuditor;

  // Which of the three lists is on screen. Derived once so the toggle precedence (audit queue
  // over admin view, for a user with both toggles on) has a single home.
  const activeView: 'validated' | 'all' | 'own' = viewValidatedSessions
    ? 'validated'
    : viewAllSessions
      ? 'all'
      : 'own';
  const { sessions, fetchStatus } = {
    validated: { sessions: validatedSessions, fetchStatus: validatedSessionsStatus },
    all: { sessions: allSessions, fetchStatus: allSessionsStatus },
    own: { sessions: ownerSessions, fetchStatus: ownerSessionsStatus },
  }[activeView];

  // The audit queue is the auditor's list; every other list is the reviewer's. Filters reset on
  // view switch, so a selection can't survive the swap.
  const reviewRole: SessionReviewRole = activeView === 'validated' ? 'auditor' : 'reviewer';
  const reviewFilterLabels = SESSION_REVIEW_FILTER_LABELS_BY_ROLE[reviewRole];

  // Fetch user's own sessions on mount (always keep fresh), and again when the archived
  // toggle changes what the server returns.
  useEffect(() => {
    dispatch(fetchSessions());
  }, [dispatch, includeArchivedSessions]);

  // Reset all filters when switching between views — a filter (e.g. a site) valid in one view
  // may match nothing in another and would otherwise silently hide every session. The view
  // toggles drop the merge picks themselves, so returning to this page keeps them.
  useEffect(() => {
    resetControls();
  }, [viewAllSessions, viewValidatedSessions, resetControls]);

  // Fetch all sessions when admin toggle is enabled
  useEffect(() => {
    if (viewAllSessions && hasElevatedAccess) {
      dispatch(fetchAllSessions(null));
    }
  }, [dispatch, viewAllSessions, hasElevatedAccess, includeArchivedSessions]);

  // Fetch the audit queue (validated sessions across all sites) when the auditor toggle is on
  useEffect(() => {
    if (viewValidatedSessions && isAuditor) {
      dispatch(fetchValidatedSessions());
    }
  }, [dispatch, viewValidatedSessions, isAuditor]);

  // Auto-load remaining pages when viewing all sessions
  useEffect(() => {
    if (viewAllSessions && allSessionsNextToken && allSessionsLoading) {
      dispatch(fetchAllSessions(allSessionsNextToken));
    }
  }, [dispatch, viewAllSessions, allSessionsNextToken, allSessionsLoading]);

  const handleViewSession = (sessionId: string) => {
    navigate(`/session/${sessionId}`);
  };

  // Merge is an admin action over the own and all-sessions lists, not the audit queue.
  const canMerge = hasElevatedAccess && activeView !== 'validated';
  const isMerging = canMerge && isMergeMode;
  // Any two sessions can be picked; the compare page says why a pair can't merge.
  const mergeSelectionViewFor = selectedIdsToMergeSelectionView(mergeSelectedIds);

  const sessionToMergeSelection = (session: SessionListDTO): SessionMergeSelection => ({
    ...mergeSelectionViewFor(session),
    label: MERGE_SELECTION_LABEL,
    onChange: () => toggleMergeSelection(session.session_id),
  });

  // In merge mode a card click picks the session instead of opening it.
  const handleCardClick = (session: SessionListDTO) => {
    if (!isMerging) {
      handleViewSession(session.session_id);
      return;
    }
    if (!mergeSelectionViewFor(session).disabled) {
      toggleMergeSelection(session.session_id);
    }
  };

  const handleCompare = () => {
    navigate(selectedIdsToComparePath(mergeSelectedIds));
  };

  const handleRetry = () => {
    if (activeView === 'validated' && isAuditor) {
      dispatch(fetchValidatedSessions());
    } else if (activeView === 'all' && hasElevatedAccess) {
      dispatch(fetchAllSessions(null));
    } else {
      dispatch(fetchSessions());
    }
  };

  const handleDeleteAll = async () => {
    const deletableSessions = sessionsToDeletable(sessions);
    const skippedCount = sessions.length - deletableSessions.length;

    const message = deleteAllCountsToConfirmation(deletableSessions.length)(skippedCount);

    if (!window.confirm(message)) return;

    setIsDeletingAll(true);
    let deleted = 0;
    let failed = 0;

    for (const session of deletableSessions) {
      setDeleteProgress(`Deleting ${deleted + failed + 1} of ${deletableSessions.length}...`);
      try {
        await dispatch(deleteSession(session.session_id)).unwrap();
        deleted++;
      } catch {
        failed++;
      }
    }

    setIsDeletingAll(false);
    setDeleteProgress('');

    if (failed === 0) {
      dispatch(showSuccess(`Deleted ${deleted} session(s) successfully`));
    } else {
      dispatch(showError(`Deleted ${deleted}, failed ${failed} session(s)`));
    }
  };

  // Distinct sites present in the current session list, for the site filter options
  const filters = { searchQuery, dateRange, selectedSites, selectedReviewStates };
  const availableSites = sessionsToAvailableSites(sessions);
  const filteredSessions = filterSessions(sessions, filters);
  const listedSessions = isMerging && hideUnmergeable
    ? filterMergeableSessions(mergeSelectedIds)(filteredSessions)
    : filteredSessions;
  const sortedSessions = sortSessionsNewestFirst(listedSessions);

  const {
    items: displayedSessions,
    currentPage: displayedPage,
    numberOfPages,
    title: paginationTitle,
  } = itemsToPagination(sortedSessions)(currentPage)(SESSIONS_PER_PAGE);
  const shouldRenderPagination = sortedSessions.length > 0;
  const SessionPaginationComponent = SessionPaginationWithCondition(
    shouldRenderPagination,
  );
  const pagination: Pagination = {
    currentPage: displayedPage,
    numberOfPages,
    disabled: viewAllSessions && allSessionsLoading,
    onChange: setCurrentPage,
    showSkipArrows: true,
  };

  useEffect(() => {
    if (currentPage !== displayedPage) {
      setCurrentPage(displayedPage);
    }
  }, [currentPage, displayedPage, setCurrentPage]);

  const hasActiveFilters = hasActiveSessionFilters(filters);
  const SessionMergeToolbar = SessionMergeToolbarWithCondition(isMerging);
  const SessionMergeModeToggle = SessionMergeModeToggleWithCondition(canMerge);
  const HideUnmergeableToggle = SessionFilterToggleWithCondition(isMerging);

  // ===== LOADING STATE =====
  if (fetchStatus === 'pending') {
    return (
      <div className="sessions-centered">
        <Loader size="large" />
      </div>
    );
  }

  // ===== ERROR STATE =====
  if (fetchStatus === 'rejected') {
    return (
      <div className="sessions-error">
        <Alert type="error">
          <h3>Error Loading Sessions</h3>
          <p>{error}</p>
          <div style={{ marginTop: '10px' }}>
            <Button onClick={handleRetry}>Retry</Button>
          </div>
        </Alert>
      </div>
    );
  }

  // ===== MAIN RENDER =====
  return (
    <div className="sessions-page">
      {/* Header */}
      <div className="sessions-header-row">
        <div>
          <h1 style={{ margin: 0, fontSize: FONT_SIZE_TITLE, fontWeight: 'bold' }}>
            Session History
          </h1>
          <p style={{ margin: '8px 0 0 0', color: '#666' }}>
            {{
              validated: 'Viewing validated sessions across all sites — filter by review state to see what still needs auditing',
              all: 'Viewing all sessions across all users',
              own: 'View and manage your incident sessions',
            }[activeView]}
          </p>
        </div>
        <Row spacing="300" alignmentVertical="center">
          <SessionMergeModeToggle
            label={MERGE_MODE_LABEL}
            checked={isMergeMode}
            onChange={setMergeMode}
          />
          {canDelete && sessions.length > 0 && activeView === 'own' && (
            <Button
              onClick={handleDeleteAll}
              size="large"
              type="tertiary"
              disabled={isDeletingAll}
            >
              {isDeletingAll ? deleteProgress || 'Deleting...' : 'Delete All'}
            </Button>
          )}
          <Button onClick={startNewSession} size="large">
            New Session
          </Button>
        </Row>
      </div>

      {/* Filters */}
      <div className="sessions-filter-section">
        <div className="sessions-search-column">
          {hasElevatedAccess && (
            <div style={{ marginBottom: '8px' }}>
              <Row spacing="200" alignmentVertical="center">
                <Toggle
                  checked={viewAllSessions}
                  onChange={(checked: boolean) => dispatch(setViewAllSessions(checked))}
                  size="small"
                />
                <Text type="b200">Show All Sessions</Text>
              </Row>
            </div>
          )}
          {/* The audit queue never includes archived sessions, so the toggle has nothing to do there. */}
          {hasElevatedAccess && activeView !== 'validated' && (
            <div style={{ marginBottom: '8px' }}>
              <Row spacing="200" alignmentVertical="center">
                <Toggle
                  checked={includeArchivedSessions}
                  onChange={(checked: boolean) => dispatch(setIncludeArchivedSessions(checked))}
                  size="small"
                />
                <Text type="b200">{INCLUDE_ARCHIVED_LABEL}</Text>
              </Row>
            </div>
          )}
          {isAuditor && (
            <div style={{ marginBottom: '8px' }}>
              <Row spacing="200" alignmentVertical="center">
                <Toggle
                  checked={viewValidatedSessions}
                  onChange={(checked: boolean) => dispatch(setViewValidatedSessions(checked))}
                  size="small"
                />
                <Text type="b200">Show Validated Sessions</Text>
              </Row>
            </div>
          )}
          <HideUnmergeableToggle
            label={HIDE_UNMERGEABLE_LABEL}
            checked={hideUnmergeable}
            onChange={setHideUnmergeable}
          />
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name, Case ID, or user alias..."
            onSubmit={() => {}}
            clearButton={false}
          />
        </div>
        <div className="sessions-date-column">
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            startLabel="Start date"
            endLabel="End date"
            monthsInView={2}
            size="medium"
          />
        </div>
        {availableSites.length > 0 && (
          <div className="sessions-site-column">
            <Text type="b300" tag="label">Sites</Text>
            <Select
              value={selectedSites}
              onChange={setSelectedSites}
              placeholder="Filter by site"
              size="medium"
            >
              {availableSites.map((code) => (
                <SelectOption key={code} value={code} label={code} />
              ))}
            </Select>
          </div>
        )}
        <div className="sessions-site-column">
          <Text type="b300" tag="label">Review state</Text>
          <Select
            value={selectedReviewStates}
            onChange={(value: SessionReviewFilter[]) => setSelectedReviewStates(value)}
            placeholder="Filter by review state"
            size="medium"
          >
            {SESSION_REVIEW_FILTERS_BY_ROLE[reviewRole].map((state) => (
              <SelectOption key={state} value={state} label={reviewFilterLabels[state]} />
            ))}
          </Select>
        </div>
      </div>

      {/* Content */}
      {sessions.length === 0 ? (
        <div className="sessions-empty-state">
          <div style={{ fontSize: FONT_SIZE_EMPTY_STATE_ICON, marginBottom: '20px' }}>
            <Icon tokens={documentTokens} />
          </div>
          {activeView === 'validated' ? (
            <>
              <h2 style={{ margin: '0 0 10px 0', color: '#666' }}>No Validated Sessions</h2>
              <p style={{ margin: '0', color: '#999' }}>
                Sessions appear here once a reviewer has validated them
              </p>
            </>
          ) : (
            <>
              <h2 style={{ margin: '0 0 10px 0', color: '#666' }}>No Sessions Yet</h2>
              <p style={{ margin: '0 0 20px 0', color: '#999' }}>
                Get started by creating your first incident session
              </p>
              <Button onClick={startNewSession} size="large">
                Create First Session
              </Button>
            </>
          )}
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '20px', color: '#666', fontSize: FONT_SIZE_BODY }}>
            {sortedSessions.length} session{sortedSessions.length !== 1 ? 's' : ''} found
            {activeView === 'validated' && (
              <span style={{ fontStyle: 'italic', marginLeft: '4px' }}>(validated, all sites)</span>
            )}
            {activeView === 'all' && (
              <span style={{ fontStyle: 'italic', marginLeft: '4px' }}>
                (all users){allSessionsLoading && ' — loading more...'}
              </span>
            )}
            {hasActiveFilters && (
              <span>
                {searchQuery && ` matching "${searchQuery}"`}
                {(dateRange[0] || dateRange[1]) && ' in selected date range'}
                {selectedSites.length > 0 && ` at ${selectedSites.join(', ')}`}
                {selectedReviewStates.length > 0 &&
                  ` (${selectedReviewStates
                    .map((state) => reviewFilterLabels[state])
                    .join(', ')})`}
              </span>
            )}
          </div>

          <SessionMergeToolbar
            hint={MERGE_TOOLBAR_HINT}
            compareLabel={selectedIdsToCompareLabel(mergeSelectedIds)}
            canCompare={selectedIdsToCanCompare(mergeSelectedIds)}
            onCompare={handleCompare}
          />

          {sortedSessions.length === 0 ? (
            <div className="sessions-no-results">
              <h3 style={{ margin: '0 0 10px 0', color: '#666' }}>No sessions found</h3>
              <p style={{ margin: '0', color: '#999' }}>
                Try adjusting your search or date range filters
              </p>
            </div>
          ) : (
            <div className="sessions-grid">
              {displayedSessions.map((session) => (
                <SessionCard
                  key={session.session_id}
                  session={session}
                  onClick={() => handleCardClick(session)}
                  showCreator={true}
                  mergeSelection={isMerging ? sessionToMergeSelection(session) : undefined}
                />
              ))}
            </div>
          )}
          <SessionPaginationComponent
            title={paginationTitle}
            pagination={pagination}
          />
        </>
      )}
    </div>
  );
};

export default SessionsList;
